/**
 * Read site/customer claims from the locally stored JWT access token.
 *
 * Browser sessions sign `active_site_id` / `active_customer_id` into the
 * access token at login and at every site/customer switch. Reading the
 * claim is purely informational on the client (the backend re-validates
 * the signed payload); we never trust it for authorization decisions.
 */

export interface AccessTokenClaims {
  active_site_id: number | null;
  active_customer_id: number | null;
}

const decodePayload = (token: string): Record<string, unknown> | null => {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  try {
    const padded = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const json = atob(padded + padding);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const toIntOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return null;
};

export const readAccessTokenClaims = (token: string | null | undefined): AccessTokenClaims => {
  if (!token) return { active_site_id: null, active_customer_id: null };
  const payload = decodePayload(token);
  if (!payload) return { active_site_id: null, active_customer_id: null };
  return {
    active_site_id: toIntOrNull(payload.active_site_id),
    active_customer_id: toIntOrNull(payload.active_customer_id),
  };
};

export const readStoredAccessTokenClaims = (): AccessTokenClaims => {
  return readAccessTokenClaims(localStorage.getItem('access_token'));
};
